import { useMutation } from "@apollo/client";
import { SIGN_OUT } from "graphql/mutations";
import { apolloClientManager } from "providers/ApolloClientProvider/ApolloClientManager";
import { AuthStatus, useAuth } from "providers/AuthProvider";

import { Toast } from "@aurem/ui";
import { useIntercom } from "providers/IntercomProvider";

export function useSignOut() {
  const { setAuthState } = useAuth();
  const { shutdown, boot } = useIntercom();
  const [signOut] = useMutation(SIGN_OUT);

  return async () => {
    try {
      await signOut();
    } catch (_) {
      //
    } finally {
      await apolloClientManager.reset();
      setAuthState({
        accessToken: null,
        refreshToken: null,
        status: AuthStatus.Unauthenticated,
      });
      shutdown();
      boot();
    }
  };
}

export type GoogleOAuthState = {
  action: "signin" | "signup";
  invitationId?: string;
  invitationType?: string;
  language?: string;
};

function stringifyOAuthState(state: GoogleOAuthState): string {
  try {
    return JSON.stringify(state);
  } catch (error) {
    Toast.error({
      title: (error as Error).message,
    });
    return JSON.stringify({ action: state.action });
  }
}

export function parseOAuthState(
  state: string | null,
): GoogleOAuthState | Error {
  if (typeof state !== "string") {
    return new TypeError("Expected string");
  }
  try {
    const parsed = JSON.parse(state);
    const hasAction = typeof parsed.action === "string";

    if (!hasAction) {
      return new Error("State object missing auth action type");
    }
    return parsed;
  } catch (err) {
    if (err instanceof Error) {
      return err;
    } else {
      return new Error("Unknown error");
    }
  }
}

export function buildGoogleOAuth2URL(state: GoogleOAuthState): string {
  const url = new URL("https://accounts.google.com");
  url.pathname = "o/oauth2/v2/auth";
  url.searchParams.set("state", stringifyOAuthState(state));
  url.searchParams.set("scope", process.env.GOOGLE_SCOPE ?? "");
  url.searchParams.set("redirect_uri", process.env.GOOGLE_REDIRECT_URI ?? "");
  url.searchParams.set("client_id", process.env.GOOGLE_CLIENT_ID ?? "");
  url.searchParams.set("access_type", "offline");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("include_granted_scopes", "true");
  return url.toString();
}
